import React, { useEffect } from 'react'
import { Box, Container } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { PackageVariant, ProductPackageDetail } from '../../types/productPageV2'
import { BrandNames } from '../../types/brand'
import { BindToFields, CssEntry } from '../../types/contentfulAttributes'
import { createContentfulClassGetter } from '../../utils/contentful'
import { useSKU } from '../../hooks/useSKUs'
import useBrand from '../../hooks/useBrand'
import useDiscountToken from '../../hooks/useDiscountToken'
import PurchaseCard from './PurchaseCard'
import PurchaseCardZipJob from './PurchaseCardZipJob'
import { useBETelemetry } from '@talentinc/gatsby-theme-ecom/hooks/useTelemetry'

export interface ProductPackageDetailProps {
  productPackageDetail: ProductPackageDetail
}

interface Props {
  productPackageRows: ProductPackageDetail[]
  css: CssEntry[] | null
}

const ProductPackagesContainer: React.FC<Props> = ({
  productPackageRows,
  css,
}) => {
  const { classes, cx } = useStyles()
  const brand = useBrand()
  const applyContentfulStyles = createContentfulClassGetter(css)
  const telemetry = useBETelemetry()

  useEffect(() => {
    telemetry.track({
      event: 'visit_plans_page',
    })
  }, [])

  return (
    <Box
      className={cx(
        {
          [classes.cards]: true,
          [classes.cardsZipjob]:
            brand.name?.toLowerCase() === BrandNames.zipJob,
          [classes.cards4]: productPackageRows.length === 4,
        },
        applyContentfulStyles(BindToFields.ContainerProductPackageRows)
      )}
    >
      {productPackageRows.map((productPackageDetail) => {
        return (
          <Container
            className={cx(
              {
                [classes.container]: true,
              },
              applyContentfulStyles(BindToFields.OutermostWrapper)
            )}
            disableGutters
            key={productPackageDetail.contentful_id}
          >
            {productPackageDetail.variant === PackageVariant.free ||
            !productPackageDetail.productPackage ? (
              <PurchaseCardWithoutSku
                productPackageDetail={productPackageDetail}
              />
            ) : (
              <PurchaseCardWithSku
                productPackageDetail={productPackageDetail}
              />
            )}
          </Container>
        )
      })}
    </Box>
  )
}

export const PurchaseCardWithoutSku: React.FC<ProductPackageDetailProps> = ({
  productPackageDetail,
}) => {
  const brand = useBrand()
  return (
    <>
      {brand.name === 'ZipJob' ? (
        <PurchaseCardZipJob {...productPackageDetail} />
      ) : (
        <PurchaseCard {...productPackageDetail} />
      )}
    </>
  )
}

export const PurchaseCardWithSku: React.FC<ProductPackageDetailProps> = ({
  productPackageDetail,
}) => {
  const urlDiscountToken = useDiscountToken()
  const sku = useSKU(
    productPackageDetail.productPackage.items,
    productPackageDetail.productPackage.planCode,
    urlDiscountToken ?? productPackageDetail.discountToken,
    productPackageDetail.productPackage.sort
  )
  const brand = useBrand()

  return (
    <>
      {brand.name === 'ZipJob' ? (
        <PurchaseCardZipJob
          {...productPackageDetail}
          planCode={sku?.plan_code}
          amount={sku?.amount}
          discountedAmount={sku?.discountedAmount}
          title={productPackageDetail.title || sku?.plan_short_name}
          formattedAmount={sku?.formattedOriginalAmount}
          formattedDiscount={sku?.formattedDiscount}
          purchasePath={sku?.purchasePath}
          sort={sku?.sort}
        />
      ) : (
        <PurchaseCard
          {...productPackageDetail}
          planCode={sku?.plan_code}
          amount={sku?.amount}
          discountedAmount={sku?.discountedAmount}
          title={productPackageDetail.title || sku?.plan_short_name}
          formattedAmount={sku?.formattedOriginalAmount}
          formattedDiscount={sku?.formattedDiscount}
          purchasePath={sku?.purchasePath}
          alternatePlan={sku?.alternate_plan}
        />
      )}
    </>
  )
}

const useStyles = makeStyles()((theme) => ({
  cards4: {
    '&&': {
      display: 'grid',
      justifyItems: 'center',
      gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
      gap: '2rem',
      '&& > div': {
        margin: 0,
        [theme.breakpoints.up('md')]: {
          width: 'unset',
        },
      },
      '& > div > div': {
        display: 'flex',
        flexDirection: 'column',
        padding: 0,

        '& > div': {
          flex: 1,
          justifyContent: 'space-between',
        },
      },
      '& > *': {
        height: '100%',
      },
      [theme.breakpoints.down('lg')]: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        gridGap: '1.5rem',
        maxWidth: '68.36%',
        margin: '0 auto',
        '& > div:last-child, & > div:nth-last-child(2)': {
          '& > div > p': {
            display: 'none',
          },
        },
      },
      [theme.breakpoints.down('md')]: {
        maxWidth: '100%',
      },
      [theme.breakpoints.down(768)]: {
        gridTemplateColumns: '1fr',
      },
    },
  },
  cardsZipjob: {
    '&&': { alignItems: 'flex-end' },
  },
  cards: {
    alignItems: 'flex-start',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    width: '100%',
    gap: '2rem',
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
    },
  },
  container: {
    margin: '0',
    maxWidth: '21.875rem',
    boxShadow: '0 9px 20px -8px rgb(32 33 36 / 30%)',
    flex: 1,

    [theme.breakpoints.down('lg')]: {
      maxWidth: '18.25em',
      width: '32%',
    },
    [theme.breakpoints.down('md')]: {
      maxWidth: 'initial',
      width: '100%',
    },
  },
  containerExpanded: {
    maxWidth: '100%',
    width: '31.75em',
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  },
}))

export default ProductPackagesContainer
